<script setup lang="ts">
  import { ref, watch } from 'vue'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import CustomerService from '@/components/svg/Illustrations/CustomerService.vue'
  import { useTranslation } from '@/composables/useTranslation'

  interface Props {
    totalCalls?: number | null
  }

  interface Emits {
    (e: 'setup'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    totalCalls: null
  })
  const emit = defineEmits<Emits>()

  const showDialog = ref(false)
  const hasCheckedFirstTime = ref(false)

  const { t } = useTranslation({
    en: {
      setupPhonebotTitle: 'Set up your phone bot',
      setupPhonebotDescription:
        'You already have incoming calls. Complete the setup so the phone bot can automatically answer, forward and log them.',
      cancel: 'Cancel',
      setupNow: 'Set up now'
    },
    de: {
      setupPhonebotTitle: 'Richten Sie Ihren Phonebot ein',
      setupPhonebotDescription:
        'Sie haben bereits eingehende Anrufe. Beenden Sie die Einrichtung, damit der Phonebot sie automatisch beantworten, weiterleiten und protokollieren kann.',
      cancel: 'Abbrechen',
      setupNow: 'Jetzt einrichten'
    },
    fr: {
      setupPhonebotTitle: 'Configurez votre assistant téléphonique',
      setupPhonebotDescription:
        'Vous recevez déjà des appels entrants. Finalisez la configuration pour permettre à votre assistant téléphonique de répondre, transférer et enregistrer automatiquement les appels.',
      cancel: 'Annuler',
      setupNow: 'Configurer maintenant'
    }
  })

  const checkAndShowDialog = () => {
    if (hasCheckedFirstTime.value) return
    if (props.totalCalls === null || props.totalCalls === 0) return

    hasCheckedFirstTime.value = true

    const hasSeenPhonebotSetup = localStorage.getItem('hasSeenPhonebotSetup')
    if (!hasSeenPhonebotSetup) {
      showDialog.value = true
    }
  }

  watch(
    () => props.totalCalls,
    () => {
      checkAndShowDialog()
    },
    { immediate: true }
  )

  // Watch for dialog close (including X button)
  watch(showDialog, (newValue, oldValue) => {
    // If dialog was open and is now closed, set the flag
    if (oldValue === true && newValue === false) {
      localStorage.setItem('hasSeenPhonebotSetup', 'true')
    }
  })

  const handleSetup = () => {
    localStorage.setItem('hasSeenPhonebotSetup', 'true')
    showDialog.value = false
    emit('setup')
  }

  const handleCancel = () => {
    localStorage.setItem('hasSeenPhonebotSetup', 'true')
    showDialog.value = false
  }
</script>

<template>
  <Dialog v-model:open="showDialog">
    <DialogContent class="max-w-2xl">
      <DialogHeader>
        <DialogTitle class="text-2xl font-semibold">
          {{ t('setupPhonebotTitle') }}
        </DialogTitle>
        <DialogDescription class="text-base font-normal leading-6 tracking-normal text-gray-600">
          {{ t('setupPhonebotDescription') }}
        </DialogDescription>
      </DialogHeader>

      <!-- Illustration -->
      <div class="flex justify-center py-8">
        <CustomerService class="h-64 w-64" />
      </div>

      <DialogFooter>
        <Button
          variant="secondary"
          size="lg"
          @click="handleCancel"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          size="lg"
          class="bg-purple-600 hover:bg-purple-700"
          @click="handleSetup"
        >
          {{ t('setupNow') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
